// STYLES
import './index.scss'

//COMPONENTES
import ButtonV3 from 'components/Buttonv3'
import ButtonIsActive from 'components/ButtonIsActive'
import { PageHeaderV2 } from 'components/pageHeaderV2'
import { ProjectContainer } from 'components/project-container'
import { RoadMap } from 'components/roadmap'
import { TacticalPanelFilterComponent } from './components/filters'
import { ParticipationBadge } from 'components/participationBadge'
import { RankingPosition } from './components/ranking-position'
import { FeedbackError } from 'components/feedback/error'
import { PaginationCustomV3 } from 'components/pagination-customerV3'
import { Loader } from 'components/loader'
import { BaseModal } from 'components/BaseModal'
import { QuotationCardsGrid } from './components/quotations-cards-grid'
import { ProductsCardsGrid } from './components/products-cards-grid'
import CountLabel from './components/countLabel'
import { QuotationModal } from './components/quotationModal'
import { ListParticipatingClientsModal } from './components/listParticipatingClientsModal'
import { ListClientsSupplierWonModal } from './components/listClientsSupplierWonModal'
import ArrowsOrderby from 'components/arrowsOrderby'
import { TableSortFilter } from './components/tableSortFilter'

// LIBS
import { MdOutlineEdit, MdOutlineFilterAlt } from 'react-icons/md'
import { useContext, useEffect, useMemo, useState } from 'react'
import { BsBoxSeam } from 'react-icons/bs'
import { FaFileDownload, FaRegClipboard, FaUsers } from 'react-icons/fa'
import { FaShop } from 'react-icons/fa6'
import { UserContext } from 'contexts/user'
import * as XLSX from 'xlsx'
import { ToastContainer, toast } from 'react-toastify'
import { IoMdInformationCircle } from 'react-icons/io'

// SERVICES
import TacticalPanelService from 'services/v3/tacticalPanel.service'

// INTERFACES
import {
    RankingProduct,
    RankingProductsResponse,
} from 'interfaces/rankingProducts'
import { RankingSellersResponse } from 'interfaces/rankingSeller'
import { RankingClientsResponse } from 'interfaces/rankingCients'
import { QuotationsInfoResponse } from 'interfaces/quotationsInfo'

// CONSTANTS
import Constants from '../../constants'

const TacticalPanel = () => {
    const [loading, setLoading] = useState<boolean>(true)
    const [loadingExport, setLoadingExport] = useState<boolean>(false)
    const [loadingSortType, setLoadingSortType] = useState<boolean>(false)
    const [viewFilters, setViewFilters] = useState<boolean>(true)
    const [activeTable, setActiveTable] = useState<string>('RANKING_PRODUCTS')
    const { user } = useContext(UserContext)
    const [rankingProducts, setRankingProducts] =
        useState<RankingProductsResponse | null>()
    const [rankingSellers, setRankingSellers] =
        useState<RankingSellersResponse | null>()
    const [rankingClients, setRankingClients] =
        useState<RankingClientsResponse | null>()
    const [quotationsInfo, setQuotationsInfo] =
        useState<QuotationsInfoResponse | null>()
    const [openProductQuotationModal, setOpenProductQuotationModal] =
        useState<boolean>(false)
    const [
        openListParticipatingClientsModal,
        setOpenListParticipatingClientsModal,
    ] = useState<boolean>(false)
    const [
        openListClientsSupplierWonModal,
        setOpenListClientsSupplierWonModal,
    ] = useState<boolean>(false)
    const [product, setProduct] = useState<RankingProduct>()

    const DEFAULT_SORT = {
        field: 'taxa_vitoria',
        sortType: 'desc',
    }

    const initialParams = {
        providerId: user?.providerId || '',
        page: 1,
        page_size: 10,
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        productId: '',
        sellerId: '',
        clientId: '',
        state: '',
        city: '',
        field: DEFAULT_SORT.field,
        sortType: DEFAULT_SORT.sortType,
    }

    const [productParams, setProductParams] = useState(initialParams)
    const [sellerParams, setSellerParams] = useState(initialParams)
    const [clientParams, setClientParams] = useState(initialParams)

    useEffect(() => {
        if (user?.providerId && productParams.providerId) {
            setLoading(true)
            loadQuotationsInfo().finally(() => setLoading(false))
        }
    }, [
        productParams.clientId,
        productParams.providerId,
        productParams.month,
        productParams.year,
        productParams.sellerId,
        productParams.productId,
        productParams.state,
        productParams.city,
        user?.providerId,
    ])

    useEffect(() => {
        if (user?.providerId && productParams.providerId === '') {
            setParamsForAll({ providerId: user.providerId })
        }
    }, [user?.providerId])

    useEffect(() => {
        if (sellerParams.providerId) {
            setLoadingSortType(true)
            loadRankingSellers().finally(() => setLoadingSortType(false))
        }
    }, [sellerParams])

    useEffect(() => {
        if (clientParams.providerId) {
            setLoadingSortType(true)
            loadRankingClients().finally(() => setLoadingSortType(false))
        }
    }, [clientParams])

    useEffect(() => {
        if (user?.providerId && productParams.providerId) {
            setLoadingSortType(true)
            loadRankingProducts().finally(() => setLoadingSortType(false))
        }
    }, [productParams])

    const isFiltersChanged = useMemo(() => {
        const normalize = (params: typeof initialParams) => {
            const { page, ...rest } = params
            return rest
        }

        const initialNormalized = normalize(initialParams)

        return (
            JSON.stringify(normalize(productParams)) !==
                JSON.stringify(initialNormalized) ||
            JSON.stringify(normalize(sellerParams)) !==
                JSON.stringify(initialNormalized) ||
            JSON.stringify(normalize(clientParams)) !==
                JSON.stringify(initialNormalized)
        )
    }, [productParams, sellerParams, clientParams, initialParams])

    const setParamsForAll = (newParams: any) => {
        setProductParams((prev) => ({ ...prev, ...newParams, page: 1 }))
        setSellerParams((prev) => ({ ...prev, ...newParams, page: 1 }))
        setClientParams((prev) => ({ ...prev, ...newParams, page: 1 }))
    }

    const handleFilterChange = (newFilters: any) => {
        setParamsForAll(newFilters)
    }

    const clearAllFilters = () => {
        setParamsForAll(initialParams)
        setActiveTable('RANKING_PRODUCTS')
    }

    const loadRankingProducts = async () => {
        try {
            const response = await TacticalPanelService.rankingProducts({
                ...productParams,
            })
            setRankingProducts(response)

            // setLoading(false)
        } catch (error) {
            console.error('Erro ao carregar ranking de produtos:', error)
            setRankingProducts(null)
        }
    }

    const loadRankingSellers = async () => {
        try {
            const response = await TacticalPanelService.rankingSellers(
                sellerParams
            )
            setRankingSellers(response)
        } catch (error) {
            console.error('Erro ao carregar ranking de vendedores:', error)
            setRankingSellers(null)
        }
    }

    const loadRankingClients = async () => {
        try {
            const response = await TacticalPanelService.rankingClients(
                clientParams
            )
            setRankingClients(response)
        } catch (error) {
            console.error('Erro ao carregar ranking de clientes:', error)
            setRankingClients(null)
        }
    }

    const loadQuotationsInfo = async () => {
        try {
            const response = await TacticalPanelService.getQuotationsInfo(
                productParams
            )
            setQuotationsInfo(response)
        } catch (error) {
            console.error('Erro ao carregar informações de cotações:', error)
            setQuotationsInfo(null)
        }
    }

    const handleButtonClick = (table: string) => {
        setActiveTable(table)
    }

    const toggleFilters = () => {
        setViewFilters(!viewFilters)
    }

    // BLOCO PARA GERAR PLANILHA EXCEL //// COMEÇO
    const fetchDataByTable = async (activeTable: string) => {
        let response: any
        let isGroupedHeader = false
        let mainColumnTitle = ''

        switch (activeTable) {
            case 'RANKING_PRODUCTS':
                response = await TacticalPanelService.rankingProducts({
                    ...productParams,
                    page: 1,
                    page_size: rankingProducts?.meta.total_records,
                })
                mainColumnTitle = 'Produto'
                break

            case 'RANKING_SELLERS':
                response = await TacticalPanelService.rankingSellers({
                    ...sellerParams,
                    page: 1,
                    page_size: rankingSellers?.meta.total_records,
                })
                mainColumnTitle = 'Vendedor'
                isGroupedHeader = true
                break

            case 'RANKING_CLIENTS':
                response = await TacticalPanelService.rankingClients({
                    ...clientParams,
                    page: 1,
                    page_size: rankingClients?.meta.total_records,
                })
                mainColumnTitle = 'Cliente'
                isGroupedHeader = true
                break

            default:
                throw new Error('Tabela ativa não reconhecida.')
        }

        if (!response || response.status !== '200' || !response.data) {
            throw new Error('Erro ao buscar dados do endpoint.')
        }

        return { data: response.data, mainColumnTitle, isGroupedHeader }
    }
    const buildWorksheetData = (
        data: any[],
        mainColumnTitle: string,
        activeTable: string,
        isGroupedHeader: boolean
    ) => {
        const headerRow1 = ['Ranking', mainColumnTitle]
        const headerRow2 = ['', '']

        // Define os grupos do header de forma reutilizável
        const groupedHeaders = [
            'QTD Recebidas',
            'QTD Ofertas',
            'QTD Perdidas',
            'QTD Ganhas',
            'Taxa de Vitória',
        ]

        // Determina o nome do segundo grupo (CLIENTES ou PRODUTOS)
        const secondGroupTitle = isGroupedHeader ? 'PRODUTOS' : 'CLIENTES'

        // Monta os títulos do cabeçalho
        headerRow1.push(
            'COTAÇÕES',
            ...Array(groupedHeaders.length - 1).fill('')
        )
        headerRow1.push(
            secondGroupTitle,
            ...Array(groupedHeaders.length - 1).fill('')
        )

        headerRow2.push(...groupedHeaders, ...groupedHeaders)

        const rows = data.map((item: any) => {
            const baseData = [
                item.rank,
                isGroupedHeader
                    ? activeTable === 'RANKING_SELLERS'
                        ? item.seller_name
                        : item.client_name
                    : item.product_name,
                item.total_cotacoes_recebidas,
                item.total_cotacoes_ofertadas,
                item.total_cotacoes_perdidas,
                item.total_cotacoes_ganhas,
                `${item.taxa_vitoria?.toFixed(2) || '0'}%`,
            ]

            const extraData = isGroupedHeader
                ? [
                      item.total_produtos_recebidos,
                      item.total_produtos_ofertados,
                      item.total_produtos_perdidos,
                      item.total_produtos_ganhos,
                      `${item.taxa_vitoria_produtos?.toFixed(2) || '0'}%`,
                  ]
                : [
                      item.total_clientes_recebidos,
                      item.total_clientes_ofertados,
                      item.total_clientes_perdedor,
                      item.total_clientes_ganhos,
                      `${item.taxa_vitoria_clientes?.toFixed(2) || '0'}%`,
                  ]

            return [...baseData, ...extraData]
        })

        return [headerRow1, headerRow2, ...rows]
    }

    const getColumnWidths = (isGroupedHeader: boolean) => {
        return isGroupedHeader
            ? [
                  { wch: 8 }, // Ranking
                  { wch: 30 }, // Vendedor/Cliente
                  { wch: 13 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 13 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 12 },
              ]
            : [
                  { wch: 8 }, // Ranking
                  { wch: 33 }, // Produto
                  { wch: 13 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 13 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 12 },
                  { wch: 12 },
              ]
    }

    const getMergedCells = (isGroupedHeader: boolean) => {
        // if (!isGroupedHeader) return []

        return [
            { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // Ranking
            { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // Nome
            { s: { r: 0, c: 2 }, e: { r: 0, c: 6 } }, // COTAÇÕES
            { s: { r: 0, c: 7 }, e: { r: 0, c: 11 } }, // PRODUTOS/CLIENTES
        ]
    }

    const exportToExcel = async (activeTable: string) => {
        setLoadingExport(true)

        try {
            const { data, mainColumnTitle, isGroupedHeader } =
                await fetchDataByTable(activeTable)

            if (!data || data.length === 0) {
                toast.info('Nenhum dado disponível para exportação no momento.')
                return
            }

            const worksheetData = buildWorksheetData(
                data,
                mainColumnTitle,
                activeTable,
                isGroupedHeader
            )

            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
            worksheet['!cols'] = getColumnWidths(isGroupedHeader)
            worksheet['!merges'] = getMergedCells(isGroupedHeader)

            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Ranking')
            XLSX.writeFile(workbook, `${activeTable}.xlsx`)

            toast.success('Planilha exportada com sucesso.')
        } catch (error) {
            console.error(error)
            toast.error(
                'Erro ao exportar para Excel. Por favor, tente novamente mais tarde.'
            )
        } finally {
            setLoadingExport(false)
        }
    }
    // BLOCO PARA GERAR PLANILHA EXCEL //// FINAL

    const getFormattedMonthYear = () => {
        const month = productParams.month
        const year = productParams.year

        if (!month || !year) return 'Mês/Ano não definidos'

        const date = new Date(year, month - 1)
        const formattedMonth = date.toLocaleString('pt-BR', { month: 'long' })

        return `Dados referentes a ${
            formattedMonth.charAt(0).toUpperCase() + formattedMonth.slice(1)
        } de ${year}.`
    }

    const handleOpenModalProductQuotation = (product) => {
        setProduct(product)
        setOpenProductQuotationModal(true)
    }
    const handleOpenListParticipatingClientsModal = (product) => {
        setProduct(product)
        setOpenListParticipatingClientsModal(true)
    }
    const handleOpenListClientsSupplierWonModal = (product) => {
        setProduct(product)
        setOpenListClientsSupplierWonModal(true)
    }

    const handleSort = (field: string) => {
        const updateParams = (prevParams: typeof productParams) => {
            const isSameField = prevParams.field === field
            const currentSort = prevParams.sortType

            if (isSameField) {
                if (currentSort === 'desc') {
                    // Segundo clique → asc
                    return {
                        ...prevParams,
                        field,
                        sortType: 'asc',
                        page: 1,
                    }
                } else if (currentSort === 'asc') {
                    // Terceiro clique → resetar para o padrão (se não for o padrão)
                    if (field !== DEFAULT_SORT.field) {
                        return {
                            ...prevParams,
                            ...DEFAULT_SORT,
                            page: 1,
                        }
                    } else {
                        // Se for o campo padrão, volta pra desc
                        return {
                            ...prevParams,
                            field,
                            sortType: 'desc',
                            page: 1,
                        }
                    }
                }
            }

            // Primeiro clique → desc
            return {
                ...prevParams,
                field,
                sortType: 'desc',
                page: 1,
            }
        }

        switch (activeTable) {
            case 'RANKING_PRODUCTS':
                setProductParams(updateParams)
                break
            case 'RANKING_SELLERS':
                setSellerParams(updateParams)
                break
            case 'RANKING_CLIENTS':
                setClientParams(updateParams)
                break
            default:
                break
        }
    }

    const getColumnOptions = () => {
        switch (activeTable) {
            case 'RANKING_PRODUCTS':
                return Constants.PRODUCT_COLUMN_OPTIONS
            case 'RANKING_SELLERS':
                return Constants.SELLER_COLUMN_OPTIONS
            case 'RANKING_CLIENTS':
                return Constants.CLIENT_COLUMN_OPTIONS
            default:
                return []
        }
    }

    const getCurrentSortField = () => {
        switch (activeTable) {
            case 'RANKING_PRODUCTS':
                return productParams.field
            case 'RANKING_SELLERS':
                return sellerParams.field
            case 'RANKING_CLIENTS':
                return clientParams.field
            default:
                return DEFAULT_SORT.field
        }
    }

    const getCurrentSortType = () => {
        switch (activeTable) {
            case 'RANKING_PRODUCTS':
                return productParams.sortType
            case 'RANKING_SELLERS':
                return sellerParams.sortType
            case 'RANKING_CLIENTS':
                return clientParams.sortType
            default:
                return DEFAULT_SORT.sortType
        }
    }

    const handleSortChange = (field: string, sortType: 'asc' | 'desc') => {
        const updatedParams = { field, sortType, page: 1 }

        switch (activeTable) {
            case 'RANKING_PRODUCTS':
                return setProductParams((prev) => ({
                    ...prev,
                    ...updatedParams,
                }))
            case 'RANKING_SELLERS':
                return setSellerParams((prev) => ({
                    ...prev,
                    ...updatedParams,
                }))
            case 'RANKING_CLIENTS':
                return setClientParams((prev) => ({
                    ...prev,
                    ...updatedParams,
                }))
        }
    }

    return (
        <ProjectContainer>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <RoadMap
                pageLinks={[
                    {
                        title: 'Painel Tático',
                        onclick: () => {},
                    },
                ]}
            ></RoadMap>

            <header>
                <PageHeaderV2 title="Painel Tático" />
            </header>

            <section className="filtersContent">
                <div className="viewFilters">
                    <p className="p">Filtros</p>
                    <ButtonV3
                        title={
                            viewFilters ? 'Ocultar Filtros' : 'Exibir Filtros'
                        }
                        Icon={
                            <MdOutlineFilterAlt className="iconButtonFilter" />
                        }
                        onClick={toggleFilters}
                    />
                </div>

                <TacticalPanelFilterComponent
                    viewFilters={viewFilters}
                    onChange={handleFilterChange}
                    cleanFilters={clearAllFilters}
                    isFiltersChanged={isFiltersChanged}
                />
            </section>

            {loading ? (
                <Loader type="inline" />
            ) : (
                <>
                    <section className="sectionDataDate">
                        <p className="ViewDataDate">
                            {getFormattedMonthYear()}
                        </p>
                    </section>
                    <section className="cotacoesContent">
                        <h2 className="titleSection">Cotações</h2>

                        <QuotationCardsGrid
                            quotationsInfo={quotationsInfo || null}
                        />
                    </section>

                    <section className="productsContent">
                        <h2 className="titleSection">Produtos</h2>
                        <ProductsCardsGrid
                            quotationsInfo={quotationsInfo || null}
                        />
                    </section>
                    <section className="buttonsContent">
                        <div className="toggleButtons">
                            <ButtonIsActive
                                children="Ranking Produtos"
                                isActive={activeTable === 'RANKING_PRODUCTS'}
                                icon={<BsBoxSeam />}
                                onClick={() =>
                                    handleButtonClick('RANKING_PRODUCTS')
                                }
                            />
                            <ButtonIsActive
                                children="Ranking Vendedores"
                                isActive={activeTable === 'RANKING_SELLERS'}
                                icon={<FaUsers />}
                                onClick={() =>
                                    handleButtonClick('RANKING_SELLERS')
                                }
                            />
                            <ButtonIsActive
                                children="Ranking Clientes"
                                isActive={activeTable === 'RANKING_CLIENTS'}
                                icon={<FaShop />}
                                onClick={() =>
                                    handleButtonClick('RANKING_CLIENTS')
                                }
                            />
                        </div>
                        <div className="buttonExport">
                            {!loadingExport ? (
                                <ButtonV3
                                    type="button"
                                    title={'Exportar'}
                                    Icon={
                                        <FaFileDownload className="iconButtonFilter" />
                                    }
                                    onClick={() => exportToExcel(activeTable)}
                                />
                            ) : (
                                <Loader type="inline" size="30px" />
                            )}
                        </div>
                    </section>

                    <section className="tableContent">
                        {loadingSortType ? <Loader /> : null}
                        <div className="sortFilterComponent">
                            <TableSortFilter
                                currentSortField={getCurrentSortField()}
                                currentSortType={getCurrentSortType()}
                                onSortChange={handleSortChange}
                                columnOptions={getColumnOptions()}
                            />
                        </div>
                        {activeTable === 'RANKING_PRODUCTS' && (
                            <div className="NewTableTacticalPanelProducts">
                                {(rankingProducts?.data?.length ?? 0) > 0 ? (
                                    <>
                                        <div className="table-wrapper">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'product_name'
                                                                )
                                                            }
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                                verticalAlign:
                                                                    'middle',
                                                            }}
                                                            rowSpan={2}
                                                        >
                                                            <div className="th-content">
                                                                Produto
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'product_name'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>

                                                        <th
                                                            colSpan={5}
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            Cotações
                                                        </th>
                                                        <th
                                                            colSpan={5}
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            Clientes
                                                        </th>
                                                        <th
                                                            rowSpan={2}
                                                            style={{
                                                                verticalAlign:
                                                                    'middle',
                                                            }}
                                                        >
                                                            Ações
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_recebidas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Recebidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'total_cotacoes_recebidas'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_ofertadas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ofertas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'total_cotacoes_ofertadas'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_perdidas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Perdidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'total_cotacoes_perdidas'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_ganhas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ganhas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'total_cotacoes_ganhas'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'taxa_vitoria'
                                                                )
                                                            }
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            <div className="th-content">
                                                                Taxa de Vitórias
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'taxa_vitoria'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>

                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_clientes_recebidos'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Recebidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'total_clientes_recebidos'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_clientes_ofertados'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ofertas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'total_clientes_ofertados'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_clientes_perdedor'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Perdidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'total_clientes_perdedor'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_clientes_ganhos'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ganhas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'total_clientes_ganhos'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'taxa_vitoria_clientes'
                                                                )
                                                            }
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            <div className="th-content">
                                                                Taxa de Vitórias
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        productParams.field ===
                                                                        'taxa_vitoria_clientes'
                                                                            ? productParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {rankingProducts?.data.map(
                                                        (product) => (
                                                            <tr
                                                                key={
                                                                    product?.product_code
                                                                }
                                                            >
                                                                <td
                                                                    data-label="Produto"
                                                                    className="tdRanking"
                                                                >
                                                                    <div className="rankingContent">
                                                                        <RankingPosition
                                                                            position={
                                                                                product.rank
                                                                            }
                                                                        />
                                                                        {product?.product_name ??
                                                                            'Nome do produto indisponível'}
                                                                    </div>
                                                                </td>
                                                                <td data-label="QTD Cot. Recebidas">
                                                                    {product?.total_cotacoes_recebidas ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Ofertas">
                                                                    {product?.total_cotacoes_ofertadas ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Cot. Perdidas">
                                                                    {product?.total_cotacoes_perdidas ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Cot. Ganhas">
                                                                    {product?.total_cotacoes_ganhas ??
                                                                        ''}
                                                                </td>
                                                                <td
                                                                    data-label="Taxa de Vitórias"
                                                                    className="tdParticipationPanelMobile"
                                                                >
                                                                    <div className="tdParticipationPanel">
                                                                        <ParticipationBadge
                                                                            participation={
                                                                                (product?.taxa_vitoria
                                                                                    ? product.taxa_vitoria
                                                                                    : 0) /
                                                                                100
                                                                            }
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td data-label="QTD Recebidas (Clientes)">
                                                                    <CountLabel
                                                                        onClick={() =>
                                                                            handleOpenListParticipatingClientsModal(
                                                                                product
                                                                            )
                                                                        }
                                                                        count={
                                                                            product?.total_clientes_recebidos ??
                                                                            0
                                                                        }
                                                                    />
                                                                </td>
                                                                <td data-label="QTD Ofertas (Clientes)">
                                                                    <CountLabel
                                                                        onClick={() =>
                                                                            handleOpenListClientsSupplierWonModal(
                                                                                product
                                                                            )
                                                                        }
                                                                        count={
                                                                            product.total_clientes_ofertados ??
                                                                            0
                                                                        }
                                                                    />
                                                                </td>
                                                                <td data-label="QTD  Pedidas (Clientes)">
                                                                    {product.total_clientes_perdedor ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Ganhas (Clientes)">
                                                                    {product.total_clientes_ganhos ??
                                                                        ''}
                                                                </td>
                                                                <td
                                                                    data-label="Taxa de Vitórias (Clientes)"
                                                                    className="tdParticipationPanelMobile"
                                                                    // style={{
                                                                    //     borderRight:
                                                                    //         '1px solid #eee',
                                                                    // }}
                                                                >
                                                                    <div className="tdParticipationPanel">
                                                                        <ParticipationBadge
                                                                            participation={
                                                                                (product.taxa_vitoria_clientes
                                                                                    ? product.taxa_vitoria_clientes
                                                                                    : 0) /
                                                                                100
                                                                            }
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td data-label="Ações">
                                                                    <IoMdInformationCircle
                                                                        title="Acessar Cotações"
                                                                        size={
                                                                            24
                                                                        }
                                                                        color="#17A2B8"
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                        }}
                                                                        onClick={() =>
                                                                            handleOpenModalProductQuotation(
                                                                                product
                                                                            )
                                                                        }
                                                                    />
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div style={{ marginTop: 16 }}>
                                            <PaginationCustomV3
                                                totalItems={
                                                    rankingProducts?.meta
                                                        .total_records ?? 0
                                                }
                                                currentPage={productParams.page}
                                                handlePageChange={(
                                                    page: number
                                                ) =>
                                                    setProductParams(
                                                        (prev) => ({
                                                            ...prev,
                                                            page,
                                                        })
                                                    )
                                                }
                                                itemsPerPage={
                                                    initialParams.page_size
                                                }
                                                itemsName="Produtos"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="error-message">
                                        <FeedbackError message="Nenhum produto encontrado." />
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTable === 'RANKING_SELLERS' && (
                            <div className="NewTableTacticalPanelProducts NewTableTacticalPanelSellers">
                                {(rankingSellers?.data?.length ?? 0) > 0 ? (
                                    <>
                                        <div className="table-wrapper">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'seller_name'
                                                                )
                                                            }
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                                verticalAlign:
                                                                    'middle',
                                                            }}
                                                            rowSpan={2}
                                                        >
                                                            <div className="th-content">
                                                                Vendedor
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'seller_name'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>

                                                        <th
                                                            colSpan={5}
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            Cotações
                                                        </th>
                                                        <th
                                                            colSpan={5}
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            Produtos
                                                        </th>
                                                        <th
                                                            rowSpan={2}
                                                            style={{
                                                                verticalAlign:
                                                                    'middle',
                                                            }}
                                                        >
                                                            Ações
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_recebidas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Recebidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'total_cotacoes_recebidas'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_ofertadas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ofertas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'total_cotacoes_ofertadas'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_perdidas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Perdidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'total_cotacoes_perdidas'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_ganhas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ganhas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'total_cotacoes_ganhas'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'taxa_vitoria'
                                                                )
                                                            }
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            <div className="th-content">
                                                                Taxa de Vitórias
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'taxa_vitoria'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>

                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_produtos_recebidos'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Recebidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'total_produtos_recebidos'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_produtos_ofertados'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ofertas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'total_produtos_ofertados'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_produtos_perdidos'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Perdidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'total_produtos_perdidos'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_produtos_ganhos'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ganhas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'total_produtos_ganhos'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'taxa_vitoria_produtos'
                                                                )
                                                            }
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            <div className="th-content">
                                                                Taxa de Vitórias
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        sellerParams.field ===
                                                                        'taxa_vitoria_produtos'
                                                                            ? sellerParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {rankingSellers?.data.map(
                                                        (seller) => (
                                                            <tr
                                                                key={
                                                                    seller.seller_code
                                                                }
                                                            >
                                                                <td
                                                                    data-label="Vendedor"
                                                                    className="tdRanking"
                                                                >
                                                                    <div className="rankingContent">
                                                                        <RankingPosition
                                                                            position={
                                                                                seller.rank
                                                                            }
                                                                        />
                                                                        {seller.seller_name ??
                                                                            ''}
                                                                    </div>
                                                                </td>
                                                                <td data-label="QTD Cot. Recebidas">
                                                                    {seller.total_cotacoes_recebidas ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Cot. Ofertadas">
                                                                    {seller.total_cotacoes_ofertadas ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Cot. Pedidas">
                                                                    {seller.total_cotacoes_perdidas ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Cot. Ganhas">
                                                                    {seller.total_cotacoes_ganhas ??
                                                                        ''}
                                                                </td>
                                                                <td
                                                                    data-label="Taxa de Vitórias Cot."
                                                                    className="tdParticipationPanelMobile"
                                                                    // style={{
                                                                    //     borderRight:
                                                                    //         '1px solid #eee',
                                                                    // }}
                                                                >
                                                                    <div className="tdParticipationPanel">
                                                                        <ParticipationBadge
                                                                            participation={
                                                                                (seller.taxa_vitoria
                                                                                    ? seller.taxa_vitoria
                                                                                    : 0) /
                                                                                100
                                                                            }
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td data-label="QTD Recebidas (Produtos)">
                                                                    {seller.total_produtos_recebidos ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Ofertas (Produtos)">
                                                                    {seller.total_produtos_ofertados ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD  Pedidas (Produtos)">
                                                                    {seller.total_produtos_perdidos ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Ganhas (Produtos)">
                                                                    {seller.total_produtos_ganhos ??
                                                                        ''}
                                                                </td>
                                                                <td
                                                                    data-label="Taxa de Vitórias (Produtos)"
                                                                    className="tdParticipationPanelMobile"
                                                                    // style={{
                                                                    //     borderRight:
                                                                    //         '1px solid #eee',
                                                                    // }}
                                                                >
                                                                    <div className="tdParticipationPanel">
                                                                        <ParticipationBadge
                                                                            participation={
                                                                                (seller.taxa_vitoria_produtos
                                                                                    ? seller.taxa_vitoria_produtos
                                                                                    : 0) /
                                                                                100
                                                                            }
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td data-label="Ações">
                                                                    <MdOutlineEdit
                                                                        size={
                                                                            24
                                                                        }
                                                                    />
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div style={{ marginTop: 16 }}>
                                            <PaginationCustomV3
                                                totalItems={
                                                    rankingSellers?.meta
                                                        .total_records ?? 0
                                                }
                                                currentPage={sellerParams.page}
                                                handlePageChange={(
                                                    page: number
                                                ) =>
                                                    setSellerParams((prev) => ({
                                                        ...prev,
                                                        page,
                                                    }))
                                                }
                                                itemsPerPage={
                                                    initialParams.page_size
                                                }
                                                itemsName="Vendedores"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="error-message">
                                        <FeedbackError message="Nenhum Vendedor encontrado." />
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTable === 'RANKING_CLIENTS' && (
                            <div className="NewTableTacticalPanelProducts NewTableTacticalPanelClients">
                                {(rankingClients?.data?.length ?? 0) > 0 ? (
                                    <>
                                        <div className="table-wrapper">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'client_name'
                                                                )
                                                            }
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                                verticalAlign:
                                                                    'middle',
                                                            }}
                                                            rowSpan={2}
                                                        >
                                                            <div className="th-content">
                                                                Cliente
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'client_name'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>

                                                        <th
                                                            colSpan={5}
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            Cotações
                                                        </th>
                                                        <th
                                                            colSpan={5}
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            Produtos
                                                        </th>
                                                        <th
                                                            rowSpan={2}
                                                            style={{
                                                                verticalAlign:
                                                                    'middle',
                                                            }}
                                                        >
                                                            Ações
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_recebidas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Recebidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'total_cotacoes_recebidas'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_ofertadas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ofertas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'total_cotacoes_ofertadas'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_perdidas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Perdidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'total_cotacoes_perdidas'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_cotacoes_ganhas'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ganhas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'total_cotacoes_ganhas'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'taxa_vitoria'
                                                                )
                                                            }
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            <div className="th-content">
                                                                Taxa de Vitórias
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'taxa_vitoria'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_produtos_recebidos'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Recebidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'total_produtos_recebidos'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_produtos_ofertados'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ofertas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'total_produtos_ofertados'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_produtos_perdidos'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Perdidas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'total_produtos_perdidos'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'total_produtos_ganhos'
                                                                )
                                                            }
                                                        >
                                                            <div className="th-content">
                                                                QTD Ganhas
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'total_produtos_ganhos'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                        <th
                                                            onClick={() =>
                                                                handleSort(
                                                                    'taxa_vitoria_produtos'
                                                                )
                                                            }
                                                            style={{
                                                                borderRight:
                                                                    '1px solid #eee',
                                                            }}
                                                        >
                                                            <div className="th-content">
                                                                Taxa de Vitórias
                                                                <ArrowsOrderby
                                                                    sortState={
                                                                        clientParams.field ===
                                                                        'taxa_vitoria_produtos'
                                                                            ? clientParams.sortType
                                                                            : ''
                                                                    }
                                                                />
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rankingClients?.data.map(
                                                        (client) => (
                                                            <tr
                                                                key={
                                                                    client.client_code
                                                                }
                                                            >
                                                                <td
                                                                    data-label="Cliente"
                                                                    className="tdRanking"
                                                                >
                                                                    <div className="rankingContent">
                                                                        <RankingPosition
                                                                            position={
                                                                                client.rank
                                                                            }
                                                                        />
                                                                        {client.client_name ??
                                                                            ''}
                                                                    </div>
                                                                </td>
                                                                <td data-label="QTD Cot. Recebidas">
                                                                    {client.total_cotacoes_recebidas ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Ofertas">
                                                                    {client.total_cotacoes_ofertadas ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Cot. Perdidas">
                                                                    {client.total_cotacoes_perdidas ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Cot. Ganhas">
                                                                    {client.total_cotacoes_ganhas ??
                                                                        ''}
                                                                </td>
                                                                <td
                                                                    data-label="Taxa de Vitórias Cot."
                                                                    className="tdParticipationPanelMobile"
                                                                >
                                                                    <div className="tdParticipationPanel">
                                                                        <ParticipationBadge
                                                                            participation={
                                                                                (client.taxa_vitoria
                                                                                    ? client.taxa_vitoria
                                                                                    : 0) /
                                                                                100
                                                                            }
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td data-label="QTD Recebidas (Produtos)">
                                                                    {client.total_produtos_recebidos ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Ofertas (Produtos)">
                                                                    {client.total_produtos_ofertados ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Perdidas (Produtos)">
                                                                    {client.total_produtos_perdidos ??
                                                                        ''}
                                                                </td>
                                                                <td data-label="QTD Ganhas (Produtos)">
                                                                    {client.total_produtos_ganhos ??
                                                                        ''}
                                                                </td>
                                                                <td
                                                                    data-label="Taxa de Vitórias (Produtos)"
                                                                    className="tdParticipationPanelMobile"
                                                                >
                                                                    <div className="tdParticipationPanel">
                                                                        <ParticipationBadge
                                                                            participation={
                                                                                (client.taxa_vitoria_produtos
                                                                                    ? client.taxa_vitoria_produtos
                                                                                    : 0) /
                                                                                100
                                                                            }
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td data-label="Ações">
                                                                    <MdOutlineEdit
                                                                        size={
                                                                            24
                                                                        }
                                                                    />
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div style={{ marginTop: 16 }}>
                                            <PaginationCustomV3
                                                totalItems={
                                                    rankingClients?.meta
                                                        .total_records ?? 0
                                                }
                                                currentPage={clientParams.page}
                                                handlePageChange={(
                                                    page: number
                                                ) =>
                                                    setClientParams((prev) => ({
                                                        ...prev,
                                                        page,
                                                    }))
                                                }
                                                itemsPerPage={
                                                    initialParams.page_size
                                                }
                                                itemsName="Clientes"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="error-message">
                                        <FeedbackError message="Nenhum Cliente encontrado." />
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                </>
            )}

            <BaseModal
                onCloseCallback={() => setOpenProductQuotationModal(false)}
                children={
                    <QuotationModal
                        user={user}
                        productId={product?.product_code as any}
                        hash={rankingProducts?.meta?.hash as string}
                        isVisible={openProductQuotationModal}
                        onClose={() => setOpenProductQuotationModal(false)}
                    />
                }
                icon={
                    <FaRegClipboard
                        size={21}
                        style={{ marginRight: 6, marginBottom: 6 }}
                    />
                }
                title={`${product?.product_name}`}
                isVisible={openProductQuotationModal}
                onClose={() => setOpenProductQuotationModal(false)}
            />
            <BaseModal
                onCloseCallback={() =>
                    setOpenListParticipatingClientsModal(false)
                }
                children={
                    <ListParticipatingClientsModal
                        user={user}
                        productId={product?.product_code as any}
                        hash={rankingProducts?.meta?.hash as string}
                        isVisible={openListParticipatingClientsModal}
                        onClose={() =>
                            setOpenListParticipatingClientsModal(false)
                        }
                    />
                }
                icon={
                    <FaRegClipboard
                        size={21}
                        style={{ marginRight: 6, marginBottom: 6 }}
                    />
                }
                title="CLIENTES RECEBIDOS"
                isVisible={openListParticipatingClientsModal}
                onClose={() => setOpenListParticipatingClientsModal(false)}
            />
            <BaseModal
                onCloseCallback={() =>
                    setOpenListClientsSupplierWonModal(false)
                }
                children={
                    <ListClientsSupplierWonModal
                        user={user}
                        productId={product?.product_code as any}
                        hash={rankingProducts?.meta?.hash as string}
                        isVisible={openListClientsSupplierWonModal}
                        onClose={() =>
                            setOpenListClientsSupplierWonModal(false)
                        }
                    />
                }
                icon={
                    <FaRegClipboard
                        size={21}
                        style={{ marginRight: 6, marginBottom: 6 }}
                    />
                }
                title="CLIENTES OFERTADOS"
                isVisible={openListClientsSupplierWonModal}
                onClose={() => setOpenListClientsSupplierWonModal(false)}
            />
        </ProjectContainer>
    )
}

export default TacticalPanel
